import type { FC } from 'react';
import React from 'react';
import cn from 'classnames';
import { Caption } from '@alfa-bank/site-caption';

import type { TCheckbox } from './models';
import { renderCheckMarkSVG } from './render-check-mark';

export const Checkbox: FC<React.PropsWithChildren<TCheckbox>> = ({
    caption = '',
    checked = false,
    children,
    className = '',
    color = 'red',
    dataTestId,
    disabled = false,
    error = false,
    forwardedRef,
    name = 'default_checkbox',
    onChange,
    size = 'm',
    styles,
    theme = 'white',
    type = 'checkmark',
    value = '',
}) => {
    const checkboxStyles = cn(
        styles.checkboxContainer,
        styles[`${theme}Theme`],
        styles[`${size}Size`],
        styles[`${type}Type`],
        {
            [styles.greenColor]: color === 'green',
            [styles.checked]: checked,
            [styles.disabled]: disabled,
            [styles.checkboxError]: error,
        }
    );

    return (
        <div className={className} data-test-id={dataTestId}>
            <label className={checkboxStyles}>
                <input
                    ref={forwardedRef}
                    checked={checked}
                    className={styles.input}
                    data-test-id={`${name}-checkbox`}
                    disabled={disabled}
                    name={name}
                    onChange={event => {
                        onChange({ checked: !checked, name, value, event });
                    }}
                    // при клике на чекбокс происходит 2 вызова евента из-за обертки в label, остановка всплытия решает эту проблему
                    onClick={event => event.stopPropagation()}
                    type="checkbox"
                    value={value}
                />
                <div
                    className={styles.checkbox}
                    data-test-id={`${name}-checkbox-area`}
                    data-test-selected={`${checked}`}
                >
                    {checked && renderCheckMarkSVG(type, size)}
                </div>
                {children && <div data-test-id={`${name}-checkbox-caption`}>{children}</div>}
            </label>
            {caption && (
                <Caption
                    className={cn(styles.caption, styles.external, {
                        [styles.captionError]: error,
                    })}
                    size="s"
                    theme={theme}
                >
                    {caption}
                </Caption>
            )}
        </div>
    );
};
